/**
 * ATL-1440 — the pure half of the API tokens screen: which translation key names a scope or an
 * audited action, and how an audit event reads as one line.
 *
 * i18next reads `.` as nesting and `:` as a namespace separator, so neither can appear in a key:
 * `write:sales` and `products.list` are looked up as `scope_write_sales` / `action_products_list`.
 */
import type { ApiAuditEvent } from './apiTokensApi'

const keyPart = (value: string): string => value.replace(/[.:]/g, '_')

export const scopeLabelKey = (scope: string): string => `apiTokens.scope_${keyPart(scope)}`

/** `null` for an action this bundle has no label for; the screen then shows the raw action. */
export const actionLabelKey = (action: string, known: (key: string) => boolean): string | null => {
  const key = `apiTokens.action_${keyPart(action)}`
  return known(key) ? key : null
}

export const AUDIT_PAGE_SIZE = 50
export const EXPIRY_CHOICES_DAYS = [30, 90, 365] as const

/** Who did it, as a translation key and its interpolation. */
export const auditActor = (
  event: Pick<ApiAuditEvent, 'actorType' | 'token' | 'user'>
): { key: string; name: string } => {
  if (event.actorType === 'agent') {
    return { key: 'apiTokens.actorAgent', name: event.token?.name ?? event.token?.tokenPrefix ?? '—' }
  }
  return { key: 'apiTokens.actorUser', name: event.user?.name ?? '—' }
}

/**
 * The cursor for the next page — the last event of this one, time AND id, because events can share
 * a timestamp — or `null` when this page was not full.
 */
export const nextAuditCursor = (
  page: readonly Pick<ApiAuditEvent, 'occurredAt' | 'id'>[]
): { occurredAt: string; id: string } | null => {
  if (page.length < AUDIT_PAGE_SIZE) return null
  const last = page[page.length - 1]
  return { occurredAt: last.occurredAt, id: last.id }
}
